import { yupResolver } from '@hookform/resolvers/yup'
import { compareAsc, DateOnly } from '@if/core/date'
import { EFinancingProgram, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { normalizeNumber } from '@if/core/utils'
import { Button, Grid, Paper, Stack } from '@mui/material'
import { Breadcrumb, PageError } from '@src/components'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useUpdateCreditRslaWorksheet } from '@src/data/api/credit-rsla-worksheet/credit-rsla-worksheet-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useBusinessErrors, useSetBusinessErrors } from '@src/data/selectors/app-config-selectors'
import {
  CreditRslaWorksheet,
  EditCreditRslaWorksheetDto,
  EditCreditRslaWorksheetDtoSchema,
} from '@src/data/types/CreditRslaWorksheet'
import { reportErrorToConsole } from '@src/services/error-logger'
import { extractWorksheetSaveErrors } from '@src/services/worksheet-save-error'
import { FieldError, FieldErrorsImpl, Merge, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Constants, Vehicle } from '../../data/types'
import { useFirstPaymentDateOptions } from '../EditWorksheet/editWorksheet-hooks'
import { useMaxPaymentAmountWithAgentLeeway, useMaxPaymentError } from '../ViewWorksheet/viewWorksheet-hooks'
import TransactionForm from './components/TransactionForm'
import VehicleForm from './components/VehicleForm'
const EditCreditRslaWorksheetPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Get worksheet from context
  const { creditApplication, worksheet: baseWorksheet } = useCreditApplicationData()
  const worksheet = baseWorksheet as CreditRslaWorksheet | undefined
  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
    reset,
  } = useForm<EditCreditRslaWorksheetDto>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: EditCreditRslaWorksheetDtoSchema.getDefault() as unknown as EditCreditRslaWorksheetDto,
    resolver: yupResolver(EditCreditRslaWorksheetDtoSchema),
  })

  // Fetch merchant using direct API call
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [updateCreditRslaWorksheet, isLoading] = useUpdateCreditRslaWorksheet()
  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    {
      path: `/Applications/${EFinancingProgram.CreditRsla}/${creditApplication?.id}/view`,
      label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
    },
    { path: '#', label: t('worksheet.financing') },
  ]
  const error = useBusinessErrors()
  const setBusinessErrors = useSetBusinessErrors()
  const paymentFrequency = watch('paymentFrequency')
  const deliveryOn = watch('deliveryOn')
  const paymentAmountForFrequency = watch('paymentAmountForFrequency')
  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(
    EPaymentPlan.RegularDailyInterests,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
  )
  const maxPaymentForFrequencyError = useMaxPaymentError(
    paymentFrequency,
    paymentAmountForFrequency,
    maxPaymentAmount,
    Constants.financingProgramConfigs[EFinancingProgram.CreditRsla].validateMaxPaymentAmountInWorksheet,
  )
  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)

  const onFormSubmitted = async (data: EditCreditRslaWorksheetDto) => {
    const expiry = new Date(creditApplication!.expiresOn)
    const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )

    if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
      setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }])
      return
    } else if (compareAsc(deliveryOnUTC, expiry) === 1) {
      setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }])
      return
    }

    data.planName = creditApplication?.applicant?.creditDecision?.planName || ''
    data.amountRequested = amountRequested
    data.status = EWorksheetStatus.Active
    data.vehicle.pricingCategory = 'average'
    const adjustedWholePrices = data.vehicle.pricing.adjustedWholePrices
    if (adjustedWholePrices.avg > 0) {
      data.vehicle.thirdPartyEvaluationValue = adjustedWholePrices.avg
    }
    try {
      await updateCreditRslaWorksheet(data)
      await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
      navigate(`/Applications/${EFinancingProgram.CreditRsla}/${creditApplication!.id}/view`, { replace: true })
    } catch (error) {
      reportErrorToConsole(error)
      setBusinessErrors(extractWorksheetSaveErrors(error, 'worksheet.saveFailed'))
    }
  }

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn: new DateOnly(deliveryOn),
    paymentFrequency,
    isHighEstimate: worksheet?.isHighEstimate ?? false,
    setValue,
    getValues,
  })

  if (!creditApplication || !worksheet) return null

  return (
    <div>
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <div style={{ height: '100%' }}>
          <Paper>
            <PageError errors={error} />
            <input type="hidden" value=" " {...register('id')} />
            <Grid
              sx={{ flexDirection: 'row' }}
              size={{
                xs: 6,
                md: 3.9,
              }}
            >
              <Stack direction="row" justifyContent="flex-end" spacing={3}>
                <div style={{ marginTop: '1%' }}>
                  <Button variant="contained" type="submit" disabled={isLoading || maxPaymentForFrequencyError != null}>
                    {t('common.save')}
                  </Button>
                </div>
              </Stack>
            </Grid>
            <VehicleForm
              defaultVehicle={worksheet.vehicle}
              merchant={merchant}
              control={control}
              getValues={getValues}
              setValue={setValue}
              register={register}
              watch={watch}
              errors={errors.vehicle as Merge<FieldError, FieldErrorsImpl<Vehicle>>}
              title={t('worksheet.vehicle')}
            />
            <TransactionForm
              control={control}
              currentCreditApplication={creditApplication}
              reset={reset}
              register={register}
              watch={watch}
              worksheet={worksheet}
              errors={errors as Merge<FieldError, FieldErrorsImpl<EditCreditRslaWorksheetDto>>}
              creditApplicationInterestRate={creditApplication.finalCreditDecision.interestRate}
              minFirstPaymentDate={minFirstPaymentDate}
              maxFirstPaymentDate={maxFirstPaymentDate}
            />
          </Paper>
        </div>
      </form>
    </div>
  )
}

export default EditCreditRslaWorksheetPage
